/**
 * Feature replay 一致性校验工具。
 *
 * 设计说明：
 * 1. 本文件只处理可序列化命令、Worker 摘要与哈希，不依赖 Viewer / Three.js / Puppy CAD 内核；
 * 2. Worker 与主线程复用同一套纯函数，避免 replay 校验规则在两端漂移；
 * 3. 校验失败代表派生几何重建结果不可写回 Viewer，也不能反向污染主文档状态。
 */

import {
    collectGeometryObjectReferencedFeatureIds,
    type FeatureReferenceGeometryObject,
} from '../../utils/feature/featureCommandDependencyUtils';
import type {
    FeatureReplayValidationResult,
} from '../../../types/feature/FeatureReplayConsistency';

export type {
    FeatureReplayValidationResult,
    FeatureReplayValidationStatus,
} from '../../../types/feature/FeatureReplayConsistency';

/**
 * Feature replay 校验输入。
 */
export interface FeatureReplayValidationInput {
    /** replay 使用的命令序列。 */
    readonly commands: readonly CadCommand[];
    /** Worker replay 后返回的几何状态摘要。 */
    readonly summary: WorkerGeometryStateSummary;
    /** 需要校验的目标特征 ID；为空时只校验 commandSeq 与 resultHash。 */
    readonly featureId?: string;
    /** 期望的命令序列哈希。 */
    readonly expectedCommandSeq?: string;
    /** 期望的特征定义 revision。 */
    readonly expectedFeatureRevision?: string;
    /** 期望的 Worker 结果哈希。 */
    readonly expectedResultHash?: string;
}

/**
 * 构建 Worker 批量 replay 校验载荷的输入。
 */
export interface CreateFeatureReplayBatchValidationInput {
    /** replay 使用的命令序列。 */
    readonly commands: readonly CadCommand[];
    /** 目标特征 ID。 */
    readonly featureId?: string;
    /** 期望的 Worker 结果哈希。 */
    readonly expectedResultHash?: string;
    /** 历史快照中记录的期望命令序列哈希。 */
    readonly expectedCommandSeq?: string;
    /** 历史快照中记录的期望 feature revision。 */
    readonly expectedFeatureRevision?: string;
}

/**
 * 稳定 JSON 值。
 */
type StableJsonValue =
    | null
    | string
    | number
    | boolean
    | readonly StableJsonValue[]
    | {readonly [key: string]: StableJsonValue};

const FEATURE_REPLAY_RESULT_HASH_PREFIX = 'rhash:v2:';

/**
 * 判断值是否为普通对象。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/**
 * 将任意可序列化输入规整为稳定 JSON 值。
 *
 * @param value 输入值。
 * @returns 稳定 JSON 值。
 */
function normalizeStableJsonValue(value: unknown): StableJsonValue {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return value as StableJsonValue;
    }

    if (typeof value === 'number') {
        // NaN / Infinity 不能作为稳定 CAD 主状态哈希的一部分，统一归一成字符串标记。
        return Number.isFinite(value) ? value : String(value);
    }

    if (Array.isArray(value)) {
        return value.map((item) => normalizeStableJsonValue(item));
    }

    if (ArrayBuffer.isView(value)) {
        return Array.from(value as unknown as ArrayLike<number>).map((item) => normalizeStableJsonValue(item));
    }

    if (isPlainObject(value)) {
        return Object.keys(value)
            .sort((left, right) => left.localeCompare(right))
            .reduce<Record<string, StableJsonValue>>((result, key) => {
                const nextValue = value[key];

                if (typeof nextValue !== 'undefined' && typeof nextValue !== 'function') {
                    result[key] = normalizeStableJsonValue(nextValue);
                }

                return result;
            }, {});
    }

    return String(value);
}

/**
 * 稳定序列化可哈希值。
 *
 * @param value 输入值。
 * @returns 稳定 JSON 字符串。
 */
function stableStringify(value: unknown): string {
    return JSON.stringify(normalizeStableJsonValue(value));
}

/**
 * 移除 replay 摘要中每次重建都会变化的运行时元数据。
 *
 * @param value 元数据值。
 * @returns 去除易变字段后的元数据值。
 */
function omitVolatileReplayMetadata(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map((item) => omitVolatileReplayMetadata(item));
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.keys(value)
        .filter((key) => key !== 'createdAt')
        .reduce<Record<string, unknown>>((result, key) => {
            result[key] = omitVolatileReplayMetadata(value[key]);

            return result;
        }, {});
}

/**
 * 计算 FNV-1a 哈希。
 *
 * @param value 输入字符串。
 * @returns 短哈希字符串。
 */
function hashString(value: string): string {
    let hash = 2166136261;

    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
}

/**
 * 计算命令序列哈希。
 *
 * @param commands 命令序列。
 * @returns 命令序列哈希。
 */
export function createFeatureCommandSeq(commands: readonly CadCommand[]): string {
    const normalizedCommands = commands.map((command, index) => ({
        index,
        id: command.id,
        type: command.type,
        params: command.params,
        seqId: command.seqId ?? null,
        status: command.status ?? null,
    }));

    return `cmdseq:${hashString(stableStringify(normalizedCommands))}`;
}

/**
 * 创建单条命令的稳定签名。
 *
 * @param command 命令。
 * @returns 命令签名。
 */
function createFeatureCommandSignature(command: CadCommand): string {
    return createFeatureCommandSeq([command]);
}

/**
 * 从指定位置开始收集命令 ID。
 *
 * @param commands 命令序列。
 * @param startIndex 起始下标。
 * @param commandIds 命令 ID 集合。
 */
function collectCommandIdsFromIndex(
    commands: readonly CadCommand[],
    startIndex: number,
    commandIds: Set<string>,
): void {
    for (let index = startIndex; index < commands.length; index += 1) {
        const commandId = commands[index]?.id;
        if (typeof commandId === 'string' && commandId.length > 0) {
            commandIds.add(commandId);
        }
    }
}

/**
 * 解析 replay 前后需要重新同步的 feature 命令 ID。
 *
 * 设计说明：
 * Feature 历史是顺序依赖链，任一命令发生变化后，其后续结果体都可能受几何依赖影响；
 * 因此 replay 同步边界取第一处命令差异位置，再只替换该位置之后的结果体，避免清空整场景。
 *
 * @param beforeCommands replay 前 Worker 当前命令序列。
 * @param afterCommands replay 后 Worker 当前命令序列。
 * @returns 需要增量替换的命令 ID 集合。
 */
export function resolveFeatureReplayAffectedCommandIds(
    beforeCommands: readonly CadCommand[],
    afterCommands: readonly CadCommand[],
): string[] {
    const commonLength = Math.min(beforeCommands.length, afterCommands.length);
    let firstChangedIndex = commonLength;

    for (let index = 0; index < commonLength; index += 1) {
        const beforeCommand = beforeCommands[index];
        const afterCommand = afterCommands[index];
        if (
            !beforeCommand
            || !afterCommand
            || createFeatureCommandSignature(beforeCommand) !== createFeatureCommandSignature(afterCommand)
        ) {
            firstChangedIndex = index;
            break;
        }
    }

    if (firstChangedIndex === commonLength && beforeCommands.length === afterCommands.length) {
        return [];
    }

    const affectedCommandIds = new Set<string>();
    collectCommandIdsFromIndex(beforeCommands, firstChangedIndex, affectedCommandIds);
    collectCommandIdsFromIndex(afterCommands, firstChangedIndex, affectedCommandIds);
    return [...affectedCommandIds];
}

/**
 * Feature replay 摘要差异解析输入。
 */
export interface ResolveFeatureReplayAffectedCommandIdsFromSummariesInput {
    /** replay 前的命令历史。 */
    readonly beforeCommands: readonly CadCommand[];
    /** replay 后的命令历史。 */
    readonly afterCommands: readonly CadCommand[];
    /** replay 前需要参与结果体同步判断的对象摘要。 */
    readonly beforeObjects: readonly FeatureReferenceGeometryObject[];
    /** replay 后需要参与结果体同步判断的对象摘要。 */
    readonly afterObjects: readonly FeatureReferenceGeometryObject[];
    /** 已由命令差异解析出的种子特征 ID；不传时内部重新计算。 */
    readonly seedCommandIds?: readonly string[];
}

/**
 * 规范化对象引用到的特征 ID。
 *
 * @param object 摘要对象。
 * @param knownCommandIds 当前 replay 前后真实存在过的命令 ID 集合。
 * @returns 过滤后的特征 ID 集合。
 */
function collectKnownObjectFeatureIds(
    object: FeatureReferenceGeometryObject | undefined,
    knownCommandIds: ReadonlySet<string>,
): string[] {
    if (!object) {
        return [];
    }

    return collectGeometryObjectReferencedFeatureIds(object)
        .filter((featureId) => knownCommandIds.has(featureId));
}

/**
 * 创建稳定引用签名。
 *
 * @param featureIds 特征 ID 集合。
 * @returns 排序后的签名。
 */
function createFeatureReferenceSignature(featureIds: readonly string[]): string {
    return [...new Set(featureIds)].sort((left, right) => left.localeCompare(right)).join('|');
}

/**
 * 对比 replay 前后对象引用集合，扩展需要回灌的受影响特征 ID。
 *
 * 设计说明：
 * 1. 命令差异只能识别“哪条命令被新增、删除或参数变化”；
 * 2. 相交实体合并/解合并时，上游特征命令可能完全没变，但它的结果体会从 merged body 变回独立 body；
 * 3. 因此增量 mesh 回灌还必须消费 Worker 摘要中的对象引用集合变化，确保 rollback、roll forward、
 *    Undo/Redo 和离线恢复都不会出现“设计树已回退但场景缺少上游实体”的分叉。
 *
 * @param input 摘要差异输入。
 * @returns 扩展后的受影响特征 ID。
 */
export function resolveFeatureReplayAffectedCommandIdsFromSummaries(
    input: ResolveFeatureReplayAffectedCommandIdsFromSummariesInput,
): string[] {
    const affectedCommandIds = new Set(
        input.seedCommandIds
            ?? resolveFeatureReplayAffectedCommandIds(input.beforeCommands, input.afterCommands),
    );
    const knownCommandIds = new Set<string>();
    input.beforeCommands.forEach((command) => knownCommandIds.add(command.id));
    input.afterCommands.forEach((command) => knownCommandIds.add(command.id));

    const beforeObjectsById = new Map(input.beforeObjects.map((object) => [object.id, object]));
    const afterObjectsById = new Map(input.afterObjects.map((object) => [object.id, object]));
    const allObjectIds = new Set([...beforeObjectsById.keys(), ...afterObjectsById.keys()]);

    allObjectIds.forEach((objectId) => {
        const beforeObject = beforeObjectsById.get(objectId);
        const afterObject = afterObjectsById.get(objectId);
        const beforeFeatureIds = collectKnownObjectFeatureIds(beforeObject, knownCommandIds);
        const afterFeatureIds = collectKnownObjectFeatureIds(afterObject, knownCommandIds);
        const beforeSignature = createFeatureReferenceSignature(beforeFeatureIds);
        const afterSignature = createFeatureReferenceSignature(afterFeatureIds);

        if (Boolean(beforeObject) === Boolean(afterObject) && beforeSignature === afterSignature) {
            return;
        }

        beforeFeatureIds.forEach((featureId) => affectedCommandIds.add(featureId));
        afterFeatureIds.forEach((featureId) => affectedCommandIds.add(featureId));
    });

    return [...affectedCommandIds].filter((featureId) => knownCommandIds.has(featureId));
}

/**
 * 查找目标特征命令。
 *
 * @param commands 命令序列。
 * @param featureId 目标特征 ID。
 * @returns 命中命令；不存在时返回 null。
 */
export function findFeatureReplayCommand(
    commands: readonly CadCommand[],
    featureId: string,
): CadCommand | null {
    return commands.find((command) => command.id === featureId) ?? null;
}

/**
 * 计算特征定义 revision。
 *
 * @param command 特征命令。
 * @returns 特征定义 revision。
 */
export function createFeatureRevision(command: CadCommand): string {
    return `frev:${hashString(stableStringify({
        id: command.id,
        type: command.type,
        params: command.params,
    }))}`;
}

/**
 * 计算 Worker replay 结果哈希。
 *
 * @param summary Worker 几何状态摘要。
 * @returns 结果哈希。
 */
export function createFeatureReplayResultHash(summary: WorkerGeometryStateSummary): string {
    const normalizedSummary = {
        objectCount: summary.objectCount,
        commandCount: summary.commandCount,
        objects: summary.objects.map((object) => ({
            id: object.id,
            type: object.type,
            metadata: omitVolatileReplayMetadata(object.metadata),
            commandId: object.commandId ?? null,
        })),
    };

    return `${FEATURE_REPLAY_RESULT_HASH_PREFIX}${hashString(stableStringify(normalizedSummary))}`;
}

/**
 * 判断 resultHash 是否为当前可比较版本。
 *
 * 设计说明：
 * 早期 resultHash 把 command history params 也纳入哈希；Feature undo/redo 引入 plain-data
 * 归一化后，同一几何结果可能因为 THREE.Vector3 与 plain object 的形态差异产生旧 hash 漂移。
 * 因此旧 `rhash:` 只作为历史元数据保留，不再参与 replay 阻断。
 *
 * @param resultHash 待判断哈希。
 * @returns 是否为当前版本 resultHash。
 */
export function isComparableFeatureReplayResultHash(resultHash: string | undefined): resultHash is string {
    return typeof resultHash === 'string' && resultHash.startsWith(FEATURE_REPLAY_RESULT_HASH_PREFIX);
}

/**
 * 创建 replay 校验失败原因。
 *
 * @param input 校验输入。
 * @param actual 实际 replay 标识。
 * @param expected 期望 replay 标识。
 * @returns 失败原因列表。
 */
function createFeatureReplayValidationReasons(
    input: FeatureReplayValidationInput,
    actual: {
        readonly commandSeq: string;
        readonly resultHash: string;
        readonly featureRevision?: string;
    },
    expected: {
        readonly commandSeq: string;
        readonly featureRevision?: string;
        readonly resultHash?: string;
    },
): string[] {
    const reasons: string[] = [];

    if (expected.commandSeq !== actual.commandSeq) {
        reasons.push(`commandSeq 不一致：expected=${expected.commandSeq}, actual=${actual.commandSeq}`);
    }

    if (
        isComparableFeatureReplayResultHash(expected.resultHash)
        && expected.resultHash !== actual.resultHash
    ) {
        reasons.push(`resultHash 不一致：expected=${expected.resultHash}, actual=${actual.resultHash}`);
    }

    if (expected.featureRevision && expected.featureRevision !== actual.featureRevision) {
        reasons.push(`featureRevision 不一致：expected=${expected.featureRevision}, actual=${actual.featureRevision ?? 'missing'}`);
    }

    if (input.featureId && !actual.featureRevision) {
        reasons.push(`未找到目标特征命令：${input.featureId}`);
    }

    return reasons;
}

/**
 * 校验 feature replay 结果。
 *
 * @param input 校验输入。
 * @returns 校验结果。
 */
export function validateFeatureReplay(
    input: FeatureReplayValidationInput,
): FeatureReplayValidationResult {
    const commandSeq = createFeatureCommandSeq(input.summary.history);
    const resultHash = createFeatureReplayResultHash(input.summary);
    const featureCommand = input.featureId
        ? findFeatureReplayCommand(input.summary.history, input.featureId)
        : null;
    const featureRevision = featureCommand ? createFeatureRevision(featureCommand) : undefined;
    const expectedCommandSeq = input.expectedCommandSeq ?? createFeatureCommandSeq(input.commands);
    const expectedFeatureCommand = input.featureId
        ? findFeatureReplayCommand(input.commands, input.featureId)
        : null;
    const expectedFeatureRevision = input.expectedFeatureRevision
        ?? (expectedFeatureCommand ? createFeatureRevision(expectedFeatureCommand) : undefined);
    const reasons = createFeatureReplayValidationReasons(
        input,
        {
            commandSeq,
            resultHash,
            featureRevision,
        },
        {
            commandSeq: expectedCommandSeq,
            featureRevision: expectedFeatureRevision,
            resultHash: input.expectedResultHash,
        },
    );

    return {
        status: reasons.length > 0 ? 'invalid' : 'valid',
        commandSeq,
        resultHash,
        featureRevision,
        featureId: input.featureId,
        reasons,
    };
}

/**
 * 为 Worker 批量 replay 构造校验载荷。
 *
 * @param input 构造输入。
 * @returns Worker 批量 replay 校验载荷。
 */
export function createFeatureReplayBatchValidation(
    input: CreateFeatureReplayBatchValidationInput,
): BatchExecuteValidation {
    const featureCommand = input.featureId
        ? findFeatureReplayCommand(input.commands, input.featureId)
        : null;

    return {
        expectedCommandSeq: input.expectedCommandSeq ?? createFeatureCommandSeq(input.commands),
        expectedFeatureId: input.featureId,
        expectedFeatureRevision: input.expectedFeatureRevision
            ?? (featureCommand ? createFeatureRevision(featureCommand) : undefined),
        expectedResultHash: isComparableFeatureReplayResultHash(input.expectedResultHash)
            ? input.expectedResultHash
            : undefined,
    };
}
